<template>
  <!-- Breadcrumb -->
  <v-row>
    <v-col cols="12">
      <v-breadcrumbs
        class="text-caption py-0 px-0"
        :items="viewModel.breadcrumbs"
      >
        <template v-slot:prepend>
          <v-icon icon="mdi-chart-bar" size="small"></v-icon>
        </template>
      </v-breadcrumbs>
    </v-col>
  </v-row>

  <!-- Información del Crédito -->
  <v-row>
    <v-col cols="12">
      <v-list style="border-left: 7px solid rgb(var(--v-theme-primary), 0.4)">
        <v-list-item class="rounded-sm pa-4">
          <!-- Template Default (Contenido Principal) -->
          <template #default>
            <v-list-item-title
              class="text-subtitle-1 font-weight-bold"
              style="white-space: normal"
            >
              {{ viewModel.credit.value?.nom }}
            </v-list-item-title>
            <v-list-item-subtitle style="white-space: normal">
              <p class="text-subtitle-2">
                <strong>ID de Crédito: </strong>
                <span class="ml-4">{{
                  viewModel.credit.value?.idcredito
                }}</span>
              </p>
            </v-list-item-subtitle>
          </template>

          <!-- Template Append (Botones de Acción) -->
          <template #append>
            <v-tooltip text="Copiar en portapapeles" location="bottom">
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon
                  variant="text"
                  class="ml-2"
                  @click="viewModel.copyToClipboard(viewModel.credit.value)"
                >
                  <v-icon size="18">mdi-content-copy</v-icon>
                </v-btn>
              </template>
            </v-tooltip>

            <!--             <v-btn
              icon
              variant="text"
              class="ml-2"
              @click="viewModel.shareCredit(viewModel.credit.value)"
            >
              <v-icon size="18">mdi-share-variant</v-icon>
            </v-btn> -->
          </template>
        </v-list-item>
      </v-list>
    </v-col>
  </v-row>

  <!-- Datos generales y cronograma -->
  <v-row>
    <!-- Información del Crédito -->
    <v-col cols="12" md="6">
      <v-card class="pa-4 rounded-sm" flat border>
        <div class="d-flex justify-space-between align-items-center">
          <div>
            <p class="text-subtitle-2 text-negative">Crédito Desembolsado</p>
            <p
              class="text-h6 text-primary font-weight-bold"
              :class="`text-${
                viewModel.credit.value?.moneda ? 'primary' : 'grey'
              }`"
            >
              {{ viewModel.formatCoin(viewModel.credit.value?.moneda) }}
              {{
                viewModel.formatFloat(viewModel.credit.value?.montodesembolsado)
              }}
            </p>
          </div>
          <div class="text-right">
            <p class="text-subtitle-2 text-negative">Saldo Capital</p>
            <p class="text-h6 text-negative font-weight-bold">
              {{ viewModel.formatCoin(viewModel.credit.value?.moneda) }}
              {{ viewModel.formatFloat(viewModel.credit.value?.saldo) }}
            </p>
          </div>
        </div>
        <v-progress-linear
          :model-value="
            viewModel.percentDisbursement(
              viewModel.credit.value?.montodesembolsado,
              viewModel.credit.value?.saldo
            )
          "
          color="amber"
          height="15"
          class="mt-4 rounded-lg"
        ></v-progress-linear>
      </v-card>
    </v-col>

    <!-- Información del Cronograma -->
    <v-col cols="12" md="6">
      <v-card class="pa-4 rounded-sm" flat border>
        <v-card-title class="text-subtitle-1 font-weight-bold px-0">
          Cronograma de Pagos
        </v-card-title>
        <v-btn
          color="primary"
          block
          flat
          variant="tonal"
          class="mt-2 text-none"
          @click="viewModel.showSchedule()"
          :loading="viewModel.loading.value"
        >
          Ver Cronograma
        </v-btn>
      </v-card>
    </v-col>
  </v-row>

  <!-- Detalle del Estado del Crédito -->
  <v-row class="mt-4">
    <v-col cols="12" md="6">
      <v-card class="pa-4 rounded-sm" flat border>
        <v-card-title class="text-subtitle-1 font-weight-bold px-0">
          Detalles del Estado
        </v-card-title>
        <v-divider></v-divider>
        <v-container fluid class="px-0">
          <v-row>
            <v-col cols="6">
              <span class="text-subtitle-2">Estado de la Cuota</span>
            </v-col>
            <v-col cols="6" class="text-right">
              <v-chip
                size="small"
                :color="
                  viewModel.getStateColor(viewModel.credit.value?.DIAS_ATRASO)
                "
              >
                {{
                  viewModel.getStateText(viewModel.credit.value?.DIAS_ATRASO)
                }}
              </v-chip>
            </v-col>

            <v-col cols="6">
              <span class="text-subtitle-2">Días de Atraso</span>
            </v-col>
            <v-col cols="6" class="text-right text-subtitle-2">
              <span v-if="viewModel.credit.value?.DIAS_ATRASO <= 0">0</span>
              <span v-else>
                {{ viewModel.credit.value?.DIAS_ATRASO }}
              </span>
            </v-col>

            <v-col cols="6">
              <span class="text-subtitle-2">Fecha de Vencimiento</span>
            </v-col>
            <v-col cols="6" class="text-right text-subtitle-2">
              <span>
                {{
                  viewModel.formatDate(
                    viewModel.credit.value?.FEC_CUOTA_X_VENCER
                  )
                }}
              </span>
            </v-col>

            <v-col cols="6">
              <span class="text-subtitle-2">Número de Cuota</span>
            </v-col>
            <v-col cols="6" class="text-right text-subtitle-2">
              <span>
                {{ viewModel.credit.value?.CUOTA_X_VENCER }} de
                {{ viewModel.credit.value?.cronogramas?.length || 0 }}
              </span>
            </v-col>

            <v-col cols="6" v-if="viewModel.credit_vence">
              <span class="text-subtitle-2">Saldo Capital</span>
            </v-col>
            <v-col
              cols="6"
              class="text-right text-subtitle-2"
              v-if="viewModel.credit_vence"
            >
              <span>
                {{ viewModel.formatCoin(viewModel.credit.value?.moneda) }}
                {{
                  viewModel.formatFloat(
                    viewModel.credit_vence.value?.capital ?? 0
                  )
                }}
              </span>
            </v-col>

            <v-col cols="6" v-if="viewModel.credit_vence">
              <span class="text-subtitle-2">Interés</span>
            </v-col>
            <v-col
              cols="6"
              class="text-right text-subtitle-2"
              v-if="viewModel.credit_vence"
            >
              <span>
                {{ viewModel.formatCoin(viewModel.credit.value?.moneda) }}
                {{
                  viewModel.formatFloat(
                    viewModel.credit_vence.value?.interes ?? 0
                  )
                }}
              </span>
            </v-col>

            <v-col cols="6" v-if="viewModel.credit_vence">
              <span class="text-subtitle-2">Mora</span>
            </v-col>
            <v-col
              cols="6"
              class="text-right text-subtitle-2"
              v-if="viewModel.credit_vence"
            >
              <span>
                {{ viewModel.formatCoin(viewModel.credit.value?.moneda) }}
                {{
                  viewModel.formatFloat(viewModel.credit_vence.value?.mora ?? 0)
                }}
              </span>
            </v-col>

            <v-col
              cols="6"
              v-if="viewModel.credit_vence"
              class="font-weight-bold text-subtitle-2"
            >
              <span>Cuota a Pagar</span>
            </v-col>
            <v-col
              cols="6"
              class="text-right text-subtitle-2"
              v-if="viewModel.credit_vence"
            >
              <span>
                {{ viewModel.formatCoin(viewModel.credit.value?.moneda) }}
                {{
                  viewModel.formatFloat(
                    viewModel.credit_vence.value?.montocuota || 0
                  )
                }}
              </span>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-col>
    <!-- Movimientos -->

    <v-col cols="12" md="6">
      <v-card class="pa-4 rounded-sm" flat border>
        <v-card-title class="text-subtitle-1 font-weight-bold px-0">
          Movimientos
        </v-card-title>
        <v-divider></v-divider>
        <div v-if="viewModel.credit.value?.movimientos.length === 0">
          <v-alert
            type="info"
            class="text-center text-subtitle-2"
            variant="tonal"
            text="Actualmente no tienes movimientos."
            border
          >
          </v-alert>
        </div>
        <v-list v-else class="overflow-y-auto" max-height="393">
          <v-list-item
            v-for="(mov, idx) in viewModel.credit.value?.movimientos"
            :key="'mov-' + idx"
            class="pa-4"
          >
            <template #prepend>
              <v-avatar
                rounded="lg"
                size="48"
                variant="tonal"
                :color="viewModel.getColor(mov.tipomov)"
              >
                <v-icon size="20">{{
                  viewModel.getIconName(mov.tipomov)
                }}</v-icon>
              </v-avatar>
            </template>
            <template #default>
              <v-list-item-title class="text-subtitle-1 font-weight-bold">
                {{ mov.CONCEPTO }}
              </v-list-item-title>
              <v-list-item-subtitle class="text-subtitle-2">
                {{ mov.FECHA_AMORTIZACION }} {{ mov.HORA_AMORTIZACION }}
              </v-list-item-subtitle>
            </template>
            <template #append>
              <span class="text-subtitle-1 font-weight-bold">
                {{ viewModel.formatCoin(viewModel.credit.value?.moneda) }}
                {{ viewModel.formatFloat(mov.MONTO_AMORTIZACION) }}
              </span>
            </template>
          </v-list-item>
        </v-list>
      </v-card>
    </v-col>
  </v-row>
  <SchedulesDialog ref="scheduleDialogRef" />
</template>

<script lang="ts" setup>
import CreditDetailViewModel from "@/viewmodels/app/CreditDetailViewModel";
import SchedulesDialog from "@/components/dialogs/SchedulesDialog.vue";
import { ref } from "vue";

const scheduleDialogRef = ref(null);
const viewModel = new CreditDetailViewModel(scheduleDialogRef);
</script>
